import { Icons } from "@stacc/flow-ui-components";
import { useEffect, useRef, useState } from "react";
import Accordion from "../../../components/Accordion/Accordion";
import { ApplicantInfo } from "../../../components/Applicant/ApplicantInfo";
import { DebtList } from "../../../components/DebtList";
import { FormNumber } from "../../../components/Input/FormNumber";
import { FormText } from "../../../components/Input/FormText";
import { FormTextArea } from "../../../components/Input/FormTextArea";
import {
  getMessageTasks,
  triggerMessageTask
} from "../../../services/flow.service";
import { Country } from "../../../types/common";
import {
  formatAmount as formatAmountWithCurrency,
  formatInterestRate
} from "../../../utils/helperFunctions";
import { CommonUserTaskNew } from "../CommonUserTaskNew";
import type { ComponentConfig } from "../types";
import {
  CreditDecisionBox,
  CreditDecisionLabel,
  CreditDecisionSpinner,
  CreditDecisionStatus,
  CreditDecisionWrapper,
  GapContainer,
  GridContainer,
  InfoContainer,
  InfoText,
  PaddingContainer,
  UpdatedOfferLabel,
  UpdatedOfferSection,
  WideFormWrapper
} from "./style";
import type {
  FormData,
  FormDataErrors,
  FormDataKeys,
  FormOptions
} from "./types";
import { validateFormData } from "./validation";

interface Debt {
  readonly type: "loan" | "card";
  readonly description: string;
  readonly amount: number;
  readonly internalRedemption?: boolean;
}
const formatAmount = (value?: number, country?: Country): string =>
  formatAmountWithCurrency(value, country) ?? "-";

const getDecisionColor = (decision: string): string => {
  switch (decision) {
    case "Green":
    case "Approved":
      return "#28a745";
    case "Yellow":
    case "Manual Review":
      return "#ffc107";
    case "Red":
    case "Declined":
    case "Error":
    case "Timeout":
      return "#dc3545";
    default:
      return "inherit";
  }
};

const getDecisionLabel = (decision: string): string => {
  switch (decision) {
    case "Green":
      return "Approved";
    case "Yellow":
      return "Manual Review";
    case "Red":
      return "Declined";
    case "Timeout":
      return "Evaluation timed out";
    default:
      return decision;
  }
};

const formOptions: FormOptions[] = [
  { label: "Total loan amount (incl. refinance)", value: "loanAmount" },
  { label: "External refinance amount", value: "refinanceAmount" },
  { label: "Startup fee", value: "startupFee" },
  { label: "Loan duration", value: "loanDuration" }
];

const UpdateApplication = ({ flow, task, save, trigger, updateCase }: any) => {
  // Flow state
  const applicationData = flow?.data?.agentApiApplication;
  const initialData = applicationData?.application;
  const offerData = flow?.data?.offer;
  const updatedValues = flow?.data?.updates?.updatedValues;
  const debtsFromUC = flow?.data?.integrations?.uc?.debts;
  const debtsFromUCCoapplicant =
    flow?.data?.integrations?.uc?.["debts-coapplicant"] ?? [];

  // Statistica keys and currency are country-specific. The evaluate credit decision
  // writes its result under `<COUNTRY>_External` / `<COUNTRY>_Scorecard`, matching the
  // process (loan-fi -> FI_*, loan-se -> SE_*).
  const country: Country | undefined = flow?.data?.country;
  const statisticaPrefix = country === Country.FI ? "FI" : "SE";
  const externalStatisticaKey = `${statisticaPrefix}_External`;
  const scorecardStatisticaKey = `${statisticaPrefix}_Scorecard`;

  const updateApplicationData =
    flow?.data?.integrations_evaluate?.statistica?.[externalStatisticaKey]
      ?.updateApplication;
  const isEvaluationDone =
    flow?.data?.integrations_evaluate?.isEvaluationDone ?? true;
  // Filter out internalRedemption debts
  const filteredDebtsFromUC = debtsFromUC?.filter(
    (debt: Debt) => !debt.internalRedemption
  );

  const applicantInfo = applicationData?.applicant;
  const coApplicantInfo = applicationData?.coapplicant
    ? { ...applicationData.coapplicant, isCoApplicant: true }
    : null;

  const {
    finalLoanAmount,
    interestRate,
    maxLoanAmount,
    effectiveInterestRate,
    monthlyCost,
    startupFee,
    termFee,
    terms,
    cashPayoutAmount,
    topUpAmount,
    refinanceAmount,
    maxCashPayoutAmount
  } = offerData || {};

  const {
    loanAmount: updatedLoanAmount,
    refinanceAmount: updatedRefinanceAmount,
    startupFee: updatedStartupFee,
    loanTerms: updatedLoanDuration,
    loanInterestRate: updatedInterestRate
  } = updatedValues || {};

  const loadedAnswer = task?.data?.updateApplication ?? {
    loanAmount: updatedLoanAmount ?? "",
    refinanceAmount: updatedRefinanceAmount ?? "",
    startupFee: updatedStartupFee ?? "",
    loanDuration: updatedLoanDuration ?? "",
    interestRate: updatedInterestRate ?? "",
    comment: ""
  };

  // Local state
  const [formData, setFormData] = useState<FormData>(loadedAnswer);
  const [errors, setErrors] = useState<FormDataErrors>({});
  const [statisticaResult, setStatisticaResult] = useState<string | null>(
    updateApplicationData?.decision ?? null
  );
  const [statisticaVariables, setStatisticaVariables] = useState<Record<
    string,
    any
  > | null>(updateApplicationData?.variables ?? null);
  const [isLoadingStatistica, setIsLoadingStatistica] =
    useState<boolean>(false);
  const [isStatisticaNotSupported, setIsStatisticaNotSupported] =
    useState<boolean>(false);

  const areAllFieldsEmpty =
    !formData.loanAmount &&
    !formData.refinanceAmount &&
    !formData.loanDuration &&
    !formData.interestRate &&
    !formData.startupFee;

  const POLLING_INTERVAL_MS = 3000;
  const POLLING_TIMEOUT_MS = 60000;
  const previousResultRef = useRef<string | null>(null);

  // Polling effect: calls updateCase() at regular intervals while loading
  useEffect(() => {
    if (isLoadingStatistica) {
      const interval = setInterval(() => {
        console.log("Polling: calling updateCase()");
        updateCase();
      }, POLLING_INTERVAL_MS);
      return () => clearInterval(interval);
    }
  }, [isLoadingStatistica, updateCase]);

  // Timeout effect: stops polling after POLLING_TIMEOUT_MS
  useEffect(() => {
    if (isLoadingStatistica) {
      const timeout = setTimeout(() => {
        console.error("Polling timed out after", POLLING_TIMEOUT_MS, "ms");
        setStatisticaResult("Timeout");
        setIsLoadingStatistica(false);
        previousResultRef.current = null;
      }, POLLING_TIMEOUT_MS);
      return () => clearTimeout(timeout);
    }
  }, [isLoadingStatistica]);

  // Effect to handle evaluation completion - detect new results by comparing with previous
  useEffect(() => {
    if (!isLoadingStatistica || previousResultRef.current === null) {
      return;
    }

    const integrationsEvaluateData = flow?.data?.integrations_evaluate;
    const externalUpdateApplication =
      integrationsEvaluateData?.statistica?.[externalStatisticaKey]
        ?.updateApplication;
    const scorecardUpdateApplication =
      integrationsEvaluateData?.statistica?.[scorecardStatisticaKey]
        ?.updateApplication;

    // Use the scorecard data when its decision is Red, otherwise use the external decision
    const updateApplicationDataFromFlow =
      scorecardUpdateApplication?.decision === "Red"
        ? scorecardUpdateApplication
        : externalUpdateApplication;

    const currentResultStr = JSON.stringify(
      updateApplicationDataFromFlow ?? {}
    );

    if (integrationsEvaluateData?.hasError) {
      console.error(
        "Error during Statistica evaluation:",
        integrationsEvaluateData.errorMessage
      );
      setStatisticaResult("Error");
      setIsLoadingStatistica(false);
      previousResultRef.current = null;
      return;
    }

    // Only accept results that are different from the previous result
    if (
      currentResultStr !== previousResultRef.current &&
      updateApplicationDataFromFlow
    ) {
      console.log("New evaluation result received");
      setIsLoadingStatistica(false);
      previousResultRef.current = null;

      setStatisticaResult(updateApplicationDataFromFlow.decision);
      setStatisticaVariables(updateApplicationDataFromFlow.variables);
    }
  }, [flow?.data?.integrations_evaluate, isLoadingStatistica]);

  const leftSideDetails = [
    {
      label: "Final loan amount",
      value: formatAmount(finalLoanAmount, country)
    },
    {
      label: "Maximum loan amount",
      value: formatAmount(maxLoanAmount, country)
    },
    {
      label: "Applied amount",
      value: formatAmount(initialData.appliedAmount, country)
    },
    {
      label: "Applied external refinance amount",
      value: formatAmount(initialData.refinanceAmount, country)
    },
    {
      label: "Cash payout amount",
      value: formatAmount(cashPayoutAmount, country)
    },
    {
      label: "Maximum cash payout amount",
      value: formatAmount(maxCashPayoutAmount, country)
    },
    {
      label: "Approved internal refinance amount",
      value: formatAmount(topUpAmount, country)
    },
    {
      label: "Approved external refinance amount",
      value: formatAmount(refinanceAmount, country)
    }
  ];

  const leftSideIcon = finalLoanAmount ? (
    <Icons.SuccessCircle />
  ) : flow?.caseData?.status === "active" ? (
    <Icons.ActiveCircle />
  ) : (
    <Icons.AbortedCircle />
  );

  const rightSideDetails = [
    { label: "Interest rate", value: formatInterestRate(interestRate) },
    {
      label: "Effective interest rate",
      value: formatInterestRate(effectiveInterestRate)
    },
    { label: "Monthly cost", value: formatAmount(monthlyCost, country) },
    { label: "Startup fee", value: formatAmount(startupFee, country) },
    { label: "Term fee", value: formatAmount(termFee, country) },
    { label: "Terms", value: terms }
  ];

  function handleComplete(): void {
    const errors = validateFormData(formData);

    if (Object.values(errors).some((error) => error.hasError === true)) {
      setErrors(errors);
      return;
    }

    setErrors({});

    const toNumberOrUndefined = (value: unknown): number | undefined => {
      if (typeof value === "number") {
        return value;
      }
      if (typeof value === "string" && value.trim().length > 0) {
        return Number(value);
      }
      return undefined;
    };

    const validatedData = {
      loanAmount: toNumberOrUndefined(formData.loanAmount),
      refinanceAmount: toNumberOrUndefined(formData.refinanceAmount),
      startupFee: toNumberOrUndefined(formData.startupFee),
      loanTerms: toNumberOrUndefined(formData.loanDuration),
      loanInterestRate: toNumberOrUndefined(formData.interestRate),
      comment: formData.comment
    };

    trigger(
      validatedData,
      () => {},
      () => {}
    );
  }

  async function handleEvaluateCredit(): Promise<void> {
    const error = validateFormData(formData);
    if (Object.values(error).some((e) => e.hasError)) {
      setErrors(error);
      return;
    }

    const toNumberOrUndefined = (value: unknown): number | undefined => {
      if (typeof value === "number") {
        return value;
      }
      if (typeof value === "string" && value.trim().length > 0) {
        return Number(value);
      }
      return undefined;
    };

    setErrors({});
    setIsLoadingStatistica(true);

    try {
      const messageTasks = await getMessageTasks(flow.data?.flowId);
      const task = messageTasks.find(
        (t: { taskType: string }) =>
          t.taskType === "credit-evaluate-application"
      );

      if (!task) {
        setIsLoadingStatistica(false);
        setIsStatisticaNotSupported(true);
        return;
      }

      const updateApplicationData = {
        loanAmount: toNumberOrUndefined(formData.loanAmount),
        refinanceAmount: toNumberOrUndefined(formData.refinanceAmount),
        startupFee: toNumberOrUndefined(formData.startupFee),
        loanTerms: toNumberOrUndefined(formData.loanDuration),
        loanInterestRate: toNumberOrUndefined(formData.interestRate)
      };

      // Store current result so we can detect when a new result arrives
      const currentResult =
        flow?.data?.integrations_evaluate?.statistica?.[externalStatisticaKey]
          ?.updateApplication;
      previousResultRef.current = JSON.stringify(currentResult ?? {});

      await triggerMessageTask(task.taskId, updateApplicationData);

      setIsStatisticaNotSupported(false);
    } catch (error) {
      console.error("Error running Statistica evaluation:", error);
      setStatisticaResult("Error");
      setIsLoadingStatistica(false);
    }
  }

  function handleSave(): void {
    save(
      { updateApplication: formData },
      () => {},
      () => {}
    );
  }

  function handleFormUpdate(name: string, value: any): void {
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  }

  const formConfig: ComponentConfig[] = [
    {
      component: (
        <GapContainer>
          <ApplicantInfo applicant={applicantInfo} />
          {coApplicantInfo && <ApplicantInfo applicant={coApplicantInfo} />}
        </GapContainer>
      ),
      order: 1
    },
    {
      component: (
        <GapContainer>
          {formOptions.map(
            ({ label, value }: { label: string; value: FormDataKeys }) => (
              <>
                <FormNumber
                  key={value}
                  label={label}
                  name={value}
                  placeholder={`Enter ${label.toLowerCase()}`}
                  value={formData[value]}
                  onChange={(number) => handleFormUpdate(value, number)}
                  hasError={!!errors[value]?.hasError}
                  errorMessage={errors?.[value]?.message}
                  required={false}
                />
              </>
            )
          )}
          <FormText
            label="Interest rate"
            name="interest rate"
            onChange={(value: string) =>
              handleFormUpdate("interestRate", value)
            }
            defaultValue={formData.interestRate}
            hasError={!!errors.interestRate?.hasError}
            errorMessage={errors?.interestRate?.message}
            placeholder="Enter interest rate"
            required={false}
          />
        </GapContainer>
      ),
      order: 2
    },
    {
      component: (
        <FormTextArea
          label="Internal comment"
          name="comment"
          placeholder={"Enter your comment here"}
          onChange={(value: string) => handleFormUpdate("comment", value)}
          defaultValue={formData.comment}
        />
      ),
      order: 3
    },
    {
      component: (
        <CreditDecisionWrapper>
          <CreditDecisionLabel>Result Credit decision:</CreditDecisionLabel>
          <CreditDecisionBox>
            <CreditDecisionStatus
              $hasBorder={statisticaResult === "Green" && !!statisticaVariables}
              style={{
                color: isLoadingStatistica
                  ? "#6E7686"
                  : isStatisticaNotSupported
                    ? "#6E7686"
                    : getDecisionColor(statisticaResult || ""),
                justifyContent: isLoadingStatistica ? "center" : "flex-start"
              }}
            >
              {isLoadingStatistica ? (
                <>
                  <CreditDecisionSpinner />
                  Evaluating credit···
                </>
              ) : isStatisticaNotSupported ? (
                "Credit evaluation is not supported for this application"
              ) : (
                getDecisionLabel(statisticaResult || "-")
              )}
            </CreditDecisionStatus>
            {statisticaResult === "Green" && statisticaVariables && (
              <UpdatedOfferSection>
                <UpdatedOfferLabel>Updated offer:</UpdatedOfferLabel>
                {[
                  {
                    label: "Final loan amount",
                    value: formatAmount(
                      Number(statisticaVariables.finalLoanAmt),
                      country
                    )
                  },
                  {
                    label: "External refinance amount",
                    value: formatAmount(
                      Number(statisticaVariables.approvedExternalRefinance),
                      country
                    )
                  },
                  {
                    label: "Startup fee",
                    value: formatAmount(
                      Number(statisticaVariables.standardFee),
                      country
                    )
                  },
                  {
                    label: "Interest rate",
                    value: formatInterestRate(
                      Number(statisticaVariables.interestRateCalc)
                    )
                  },
                  {
                    label: "Loan duration",
                    value: statisticaVariables.finalPaymentTerm ?? "-"
                  }
                ].map(({ label, value }) => (
                  <InfoContainer key={label}>
                    <InfoText>{label}</InfoText>
                    <InfoText>{value}</InfoText>
                  </InfoContainer>
                ))}
              </UpdatedOfferSection>
            )}
          </CreditDecisionBox>
        </CreditDecisionWrapper>
      ),
      order: 4,
      hide:
        !statisticaResult && !isLoadingStatistica && !isStatisticaNotSupported
    }
  ];

  const infoConfig = [
    {
      component: (
        <Accordion title="Summary" startsExpanded hasChevron={false}>
          <PaddingContainer>
            <GridContainer>
              <div className="left-side">
                {leftSideDetails.map(({ label, value }) => (
                  <InfoContainer key={label}>
                    <InfoText>{label}</InfoText>
                    <InfoText>{value}</InfoText>
                  </InfoContainer>
                ))}
              </div>
              <div className="right-side">
                {rightSideDetails.map(({ label, value }) => (
                  <InfoContainer key={label}>
                    <InfoText>{label}</InfoText>
                    <InfoText>{value}</InfoText>
                  </InfoContainer>
                ))}
                <InfoContainer>
                  <InfoText>Offer given</InfoText>
                  <InfoText>{leftSideIcon}</InfoText>
                </InfoContainer>
              </div>
            </GridContainer>
          </PaddingContainer>
        </Accordion>
      ),
      order: 1
    }
  ];

  if (filteredDebtsFromUC?.length > 0) {
    infoConfig.push({
      component: <DebtList debts={filteredDebtsFromUC} />,
      order: 2
    });
  }

  if (debtsFromUCCoapplicant?.length > 0) {
    infoConfig.push({
      component: (
        <DebtList
          debts={debtsFromUCCoapplicant}
          title="Debts from UC - Coapplicant"
        />
      ),
      order: 3
    });
  }

  return (
    <WideFormWrapper>
      <CommonUserTaskNew
        handleComplete={handleComplete}
        handleSave={handleSave}
        infoConfig={infoConfig}
        formConfig={formConfig}
        overrideMasonryColumns={1}
        handleEvaluateCredit={handleEvaluateCredit}
        disableButtons={isLoadingStatistica || !isEvaluationDone}
        disablePrimary={isLoadingStatistica || !isEvaluationDone}
        disableEvaluateCredit={
          isLoadingStatistica || !isEvaluationDone || areAllFieldsEmpty
        }
      />
    </WideFormWrapper>
  );
};

export default UpdateApplication;
